import type { GameTypePlugin, GameRenderer, GameConfigurator, GameRendererCapabilities } from '../../interfaces/game-type-plugin.interface';
import { GameBoardRenderer } from './game-board-renderer';
import type { GameBoardSettings } from './game-board.types';

/**
 * Game Board Plugin
 *
 * Implements the GameTypePlugin interface for board game experiences.
 * Monopoly-inspired board with dice rolling and prize spaces.
 */
export class GameBoardPlugin implements GameTypePlugin {
  readonly id = 'game-board';
  readonly name = 'Game Board';
  readonly description = 'Board game with dice rolling and customizable prize spaces';
  readonly version = '1.0.0';

  readonly capabilities: GameRendererCapabilities = {
    mode: 'html-in-3d',          // HTML board + 3D die + pawn
    requires3DScene: true,       // Needs 3D die, pawn, board base
    requiresHTMLContainer: true, // Needs HTML for board layout
    supportsAR: true,            // Fully supports AR mode
    supportsVR: false            // VR not yet supported
  };

  createRenderer(): GameRenderer {
    return new GameBoardRenderer();
  }

  createConfigurator(): GameConfigurator {
    return {
      getDefaultConfig: (): GameBoardSettings => ({
        type: 'game-board',
        orientationMode: 'flat',
        extendedPlayProbability: 0.5,
        prizeColors: undefined, // Will use DEFAULT_PRIZE_COLORS
        logoUrl: undefined,
        centerText: undefined,
        castShadow: true,
        enableCameraControls: false
      }),
      validateConfig: (config: GameBoardSettings) => {
        const errors: string[] = [];

        if (config.extendedPlayProbability !== undefined) {
          if (config.extendedPlayProbability < 0 || config.extendedPlayProbability > 1) {
            errors.push('extendedPlayProbability must be between 0 and 1');
          }
        }

        return errors;
      },
      getConfigSchema: () => ({
        sections: [
          {
            title: 'Game Settings',
            fields: [
              // Future: Add form fields for game board configurator
            ]
          }
        ]
      })
    };
  }
}
